/** Ready-made shapes, for kids who want to play before they want to draw. */

import type { Body } from './body.ts';
import type { Stroke } from './stroke.ts';
import type { Vec } from './vec.ts';
import { createBody } from './body.ts';
import { regularPolygon } from './geom.ts';

export type ToyId = 'ball' | 'block' | 'star' | 'triangle';

export const TOY_ORDER: ToyId[] = ['ball', 'block', 'star', 'triangle'];

export const TOY_LABEL: Record<ToyId, string> = {
  ball: 'Ball',
  block: 'Block',
  star: 'Star',
  triangle: 'Triangle',
};

function starPoints(outer: number, inner: number, spikes = 5): Vec[] {
  const pts: Vec[] = [];
  for (let i = 0; i < spikes * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = -Math.PI / 2 + (i / (spikes * 2)) * Math.PI * 2;
    pts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
  }
  return pts;
}

function outlineOf(id: ToyId, size: number): Vec[] {
  switch (id) {
    case 'ball':
      return regularPolygon(16, size);
    case 'block':
      return regularPolygon(4, size * 1.05, Math.PI / 4);
    case 'star':
      return starPoints(size * 1.15, size * 0.5);
    case 'triangle':
      return regularPolygon(3, size * 1.15);
  }
}

/**
 * Build a toy at a world position. The star's outline is drawn with its
 * spikes but collides as its convex hull — kids read the spikes, physics
 * stays stable.
 */
export function createToy(id: ToyId, at: Vec, color: string, size = 34): Body {
  const outline = outlineOf(id, size).map((p) => ({ x: p.x + at.x, y: p.y + at.y }));
  const closed = [...outline, outline[0]];
  const stroke: Stroke = {
    brush: 'marker',
    color: 'rgba(44, 37, 69, 0.85)',
    width: 5,
    points: closed,
    speeds: closed.map(() => 0),
    seed: 1,
    fill: color,
  };
  return createBody(outline, [stroke], {
    behavior: id === 'ball' ? 'bouncy' : 'normal',
    angVel: (Math.random() - 0.5) * 3,
    vel: { x: (Math.random() - 0.5) * 120, y: 0 },
  });
}
